import { useMemo } from 'react';
import { Alert, Button, Card, Col, Empty, List, Row, Segmented, Space, Spin, Statistic, Tag, Typography } from 'antd';
import { Column } from '@ant-design/plots';
import type { CalendarEvent, LeadTodo, LeadTodoPriority, PipelineHistoryResponse } from './types';
import type { LeadTier } from './constants';
import {
	formatAmount,
	formatDateTimeFull,
	formatDateTimeShort,
	statusDefinitions,
	statusLabelMap,
	statusChartColors,
	todoPriorityColors,
	todoPriorityLabels
} from './constants';

const { Text } = Typography;

const PIPELINE_WINDOW_OPTIONS = [
	{ label: '7 dage', value: 7 },
	{ label: '14 dage', value: 14 },
	{ label: '30 dage', value: 30 }
];

const IGNORED_PIPELINE_STATUSES = new Set(['REFERRED']);

type DashboardPageProps = {
	earned: number;
	activeLeadCount: number;
	tierCounts: Record<LeadTier, number>;
	upcomingMeetings: CalendarEvent[];
	upcomingMeetingsLoading: boolean;
	meetingWindowDays: number;
	upcomingTodos: LeadTodo[];
	upcomingTodosLoading: boolean;
	todoWindowDays: number;
	canManageTodos: boolean;
	showPipelineHistory: boolean;
	pipelineHistory: PipelineHistoryResponse | null;
	pipelineHistoryLoading: boolean;
	pipelineHistoryError: string | null;
	pipelineHistoryWindow: number;
	onPipelineWindowChange: (days: number) => void;
	onOpenLead: (leadId: string) => void;
	onGoToCalendar: () => void;
	onGoToTodos: () => void;
};

export default function DashboardPage({
	earned,
	activeLeadCount,
	tierCounts,
	upcomingMeetings,
	upcomingMeetingsLoading,
	meetingWindowDays,
	upcomingTodos,
	upcomingTodosLoading,
	todoWindowDays,
	canManageTodos,
	showPipelineHistory,
	pipelineHistory,
	pipelineHistoryLoading,
	pipelineHistoryError,
	pipelineHistoryWindow,
	onPipelineWindowChange,
	onOpenLead,
	onGoToCalendar,
	onGoToTodos
}: DashboardPageProps) {
	const pipelinePoints = pipelineHistory?.points;
	const pipelineSeries = useMemo(() => {
		if (!pipelinePoints || pipelinePoints.length === 0) {
			return [] as Array<{
				status: string;
				label: string;
				color: string;
				points: { day: string; count: number }[];
				latestCount: number;
				dailyPoints: { day: string; value: number }[];
				latestDailyCount: number;
				totalCount: number;
			}>;
		}
		const fallbackStatuses = statusDefinitions.map((item) => item.value);
		const statuses =
			Array.isArray(pipelineHistory?.statuses) && pipelineHistory.statuses.length > 0
				? pipelineHistory.statuses
				: fallbackStatuses;
		return statuses
			.filter((status) => !IGNORED_PIPELINE_STATUSES.has(status))
			.map((status) => {
			const seriesPoints = pipelinePoints
				.filter((point) => point.status === status)
				.map((point) => ({ day: point.day, count: point.count }))
				.sort((a, b) => a.day.localeCompare(b.day));
			const latestCount = seriesPoints.length > 0 ? seriesPoints[seriesPoints.length - 1].count : 0;
			let previousCount = 0;
			let latestDailyCount = 0;
			let totalCount = 0;
			const dailyPoints = seriesPoints.map((point) => {
				const delta = Math.max(point.count - previousCount, 0);
				previousCount = point.count;
				latestDailyCount = delta;
				totalCount += delta;
				return { day: point.day, value: delta };
			});
			return {
				status,
				label: statusLabelMap.get(status) || status,
				color: statusChartColors[status] || '#5B8FF9',
				points: seriesPoints,
				dailyPoints,
				latestCount,
				latestDailyCount,
				totalCount
			};
			});
	}, [pipelinePoints, pipelineHistory?.statuses]);

	const pipelineColorMap = useMemo(() => {
		return pipelineSeries.reduce((acc, series) => {
			acc[series.status] = series.color;
			return acc;
		}, {} as Record<string, string>);
	}, [pipelineSeries]);

	const pipelineChartData = useMemo(() => {
		return pipelineSeries.flatMap((series) =>
			series.dailyPoints.map((point) => ({
				day: point.day,
				value: point.value,
				status: series.status,
				label: series.label
			}))
		);
	}, [pipelineSeries]);

	const legendTotals = useMemo(() => {
		return pipelineSeries.reduce((acc, series) => {
			acc[series.label] = series.totalCount || 0;
			return acc;
		}, {} as Record<string, number>);
	}, [pipelineSeries]);

	const hasPipelineCounts = pipelineChartData.some((point) => point.value > 0);

	return (
		<Space direction="vertical" size={24} style={{ width: '100%' }}>
			{showPipelineHistory && (
				<Card
					title="Pipeline-status"
					extra={
						<Segmented
							size="small"
							value={pipelineHistoryWindow}
							options={PIPELINE_WINDOW_OPTIONS}
							onChange={(value) => onPipelineWindowChange(Number(value))}
						/>
					}
				>
					{pipelineHistoryError ? (
						<Alert type="error" message={pipelineHistoryError} showIcon />
					) : pipelineHistoryLoading ? (
						<div style={{ padding: 24, textAlign: 'center' }}>
							<Spin />
						</div>
					) : !hasPipelineCounts ? (
						<Empty
							description="Ingen pipeline-data i den valgte periode"
							image={Empty.PRESENTED_IMAGE_SIMPLE}
						/>
					) : (
						<Space direction="vertical" size={16} style={{ width: '100%' }}>
							<Space wrap>
								{pipelineSeries.map((series) => (
									<Tag
										key={series.status}
										style={{
											padding: '4px 12px',
											borderColor: series.color,
											borderWidth: 1,
											borderStyle: 'solid',
											backgroundColor: 'transparent',
											color: series.color
										}}
									>
										<Space size={8}>
											<span>{series.label}</span>
											<Text type="secondary" style={{ color: 'inherit' }}>
												{series.totalCount} i alt
											</Text>
										</Space>
									</Tag>
								))}
							</Space>
							<Column
								data={pipelineChartData}
								xField="day"
								yField="value"
								seriesField="label"
								colorField="status"
								isGroup
								color={({ status }: { status: string }) =>
									pipelineColorMap[status] || statusChartColors[status] || '#5B8FF9'}
								columnStyle={{ radius: [2, 2, 0, 0] }}
								legend={false}
								xAxis={{
									type: 'cat',
									label: {
										formatter: (value: string) => value.slice(5)
									}
								}}
								yAxis={{
									min: 0,
									nice: true
								}}
								tooltip={{
									shared: true,
									fields: ['label', 'value'],
									formatter: (datum: { label: string; value: number }) => ({
										name: datum.label,
										value: datum.value
									})
								}}
							/>
						</Space>
					)}
				</Card>
			)}

			<Row gutter={16}>
				<Col>
					<Statistic
						title="Samlet provision"
						value={earned}
						formatter={(value) => formatAmount(value as number, 'DKK')}
					/>
				</Col>
				<Col>
					<Statistic title="Aktive leads" value={activeLeadCount} />
				</Col>
				<Col>
					<Statistic title="Leads" value={tierCounts.LEAD} />
				</Col>
				<Col>
					<Statistic title="Igangværende" value={tierCounts.IN_PROGRESS} />
				</Col>
				<Col>
					<Statistic title="Kunder" value={tierCounts.CUSTOMER} />
				</Col>
			</Row>

			<Row gutter={[16, 16]}>
				<Col xs={24} lg={12}>
					<Card
						title={`Kommende møder · næste ${meetingWindowDays} dage`}
						extra={
							<Button type="link" size="small" onClick={onGoToCalendar}>
								Gå til kalender
							</Button>
						}
					>
						{upcomingMeetingsLoading ? (
							<div style={{ padding: 24, textAlign: 'center' }}>
								<Spin />
							</div>
						) : upcomingMeetings.length === 0 ? (
							<Empty
								description="Ingen planlagte møder i intervallet"
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
						) : (
							<List
								dataSource={upcomingMeetings}
								rowKey={(item) => item.id}
								renderItem={(event) => {
									const leadId = event.leadId;
									return (
										<List.Item
											actions={
												leadId
													? [
															<Button key="open-lead" size="small" onClick={() => onOpenLead(leadId)}>
																Åbn lead
															</Button>
														]
													: undefined
											}
										>
											<List.Item.Meta
												title={event.title || event.leadCompanyName || 'Møde'}
												description={
													<Space direction="vertical" size={0}>
														<Text strong>{formatDateTimeFull(event.startAt) || 'Ukendt tidspunkt'}</Text>
														{event.leadCompanyName && (
															<Text type="secondary">{event.leadCompanyName}</Text>
														)}
														{event.location && <Text type="secondary">{event.location}</Text>}
													</Space>
												}
											/>
										</List.Item>
									);
								}}
							/>
						)}
					</Card>
				</Col>

				{canManageTodos && (
					<Col xs={24} lg={12}>
						<Card
							title={`Kommende todos · næste ${todoWindowDays} dage`}
							extra={
								<Button type="link" size="small" onClick={onGoToTodos}>
									Se todos
								</Button>
							}
						>
							{upcomingTodosLoading ? (
								<div style={{ padding: 24, textAlign: 'center' }}>
									<Spin />
								</div>
							) : upcomingTodos.length === 0 ? (
								<Empty
									description="Ingen kommende opgaver i intervallet"
									image={Empty.PRESENTED_IMAGE_SIMPLE}
								/>
							) : (
								<List
									dataSource={upcomingTodos}
									rowKey={(item) => item.id}
									renderItem={(todo) => {
										const priority = (todo.priority || 'MEDIUM') as LeadTodoPriority;
										const leadId = todo.lead_id;
										return (
											<List.Item
												actions={
													leadId
														? [
																<Button key="todo-open-lead" size="small" onClick={() => onOpenLead(leadId)}>
																	Åbn lead
																</Button>
															]
														: undefined
												}
											>
												<List.Item.Meta
													title={
														<Space size={8} align="center">
															<Tag color={todoPriorityColors[priority]}>
																{todoPriorityLabels[priority]}
															</Tag>
															<span>{todo.title}</span>
														</Space>
													}
													description={
														<Space direction="vertical" size={0}>
															<Text strong>
																{todo.due_at ? formatDateTimeShort(todo.due_at) : 'Ingen deadline'}
															</Text>
															{todo.lead_company_name && (
																<Text type="secondary">{todo.lead_company_name}</Text>
															)}
															{todo.assigned_to_name && (
																<Text type="secondary">{todo.assigned_to_name}</Text>
															)}
														</Space>
													}
												/>
											</List.Item>
										);
									}}
								/>
							)}
						</Card>
					</Col>
				)}
			</Row>
		</Space>
	);
}

